import { useState, FormEvent } from 'react';

const PROJECT_TYPES = [
  'Custom Wash Bay',
  'Front Load Cabinet Washer',
  'Conveyor Inline System',
  'Powder Coat System',
  'Dry Blast Cabinet',
  'Immersion Parts Washer',
  'Top Loading Washer',
  'Ultrasonic Tank',
  'Vapor Degreaser',
  'Other / Not Sure Yet',
];

type Status = 'idle' | 'sending' | 'sent' | 'error';

export default function FinalCTA() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const message = formData.get('message') as string;
    if (message && message.length > 500) return;

    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, value.toString()));

    setStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d71uguuuimmp87kg1ov0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('sent');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="relative bg-[#080808] pt-0 pb-24 overflow-hidden">

      {/* Cinematic background hero strip */}
      <div className="relative h-80 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/6508ab85eaf80c14b29c7c06df6d9f1f.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/40 via-[#080808]/60 to-[#080808]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/80 via-transparent to-[#080808]/80" />

        {/* Centered CTA headline overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <span className="text-gold text-xs font-semibold tracking-[0.4em] uppercase font-sans mb-4">Get Started</span>
          <h2 className="font-serif text-5xl lg:text-6xl text-white leading-tight mb-4">
            Build a System That Performs<br />
            at the <em className="not-italic text-gold">Highest Level</em>
          </h2>
          <p className="text-white/50 font-sans text-lg max-w-2xl">
            Partner with a team that delivers precision, compliance, and long-term reliability.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">

        {/* Quick contact strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 -mt-4 mb-16 relative z-10">
          {[
            { icon: 'ri-phone-line', title: 'Call Us Direct', value: '866-540-7575', href: 'tel:8665407575', sub: 'Same business day response' },
            { icon: 'ri-mail-line', title: 'Email Our Team', value: 'sales@kc-qualitysystems.com', href: 'mailto:sales@kc-qualitysystems.com', sub: 'We respond within 24 hours' },
            { icon: 'ri-time-line', title: 'Business Hours', value: 'Mon–Fri: 8am – 5pm CST', href: null, sub: 'Saturday & Sunday closed' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-5 bg-[#0d0d0d] border border-[#1e1e1e] rounded-xl hover:border-gold/20 transition-colors group">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gold/8 border border-gold/15 flex-shrink-0 group-hover:bg-gold/12 transition-colors">
                <i className={`${item.icon} text-gold text-lg`} />
              </div>
              <div>
                <p className="text-white/35 text-[10px] font-sans tracking-widest uppercase mb-0.5">{item.title}</p>
                {item.href ? (
                  <a href={item.href} className="text-white text-sm font-semibold font-sans hover:text-gold transition-colors">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-white text-sm font-semibold font-sans">{item.value}</p>
                )}
                <p className="text-white/25 text-xs font-sans mt-0.5">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main form card */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

          {/* Left: form context */}
          <div className="lg:col-span-2">
            <h3 className="font-serif text-3xl text-white mb-5 leading-tight">
              Schedule Your<br />System Consultation
            </h3>
            <p className="text-white/40 font-sans text-sm leading-relaxed mb-8">
              Fill out the form and our engineering team will reach out within one business day to discuss your requirements and recommend the right solution.
            </p>

            <div className="space-y-4">
              {[
                { icon: 'ri-checkbox-circle-line', text: 'No obligation — just expert guidance' },
                { icon: 'ri-checkbox-circle-line', text: 'Custom quote tailored to your operation' },
                { icon: 'ri-checkbox-circle-line', text: '25+ years of engineering expertise' },
                { icon: 'ri-checkbox-circle-line', text: 'American-built systems, nationwide delivery' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <i className={`${item.icon} text-gold`} />
                  <span className="text-white/50 text-sm font-sans">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-[#1e1e1e]">
              <div className="flex items-center gap-4">
                <img
                  src="https://www.kc-qualitysystems.com/wp-content/uploads/2015/04/Made-in-the-USA-2.png"
                  alt="Made in USA"
                  className="h-14 w-14 object-contain"
                />
                <img
                  src="https://www.kc-qualitysystems.com/wp-content/uploads/2019/01/Quality-Logo-150x150.png"
                  alt="Quality Badge"
                  className="h-14 w-14 object-contain"
                />
                <div className="ml-2">
                  <p className="text-white font-semibold font-sans text-sm">Proudly American Made</p>
                  <p className="text-white/35 text-xs font-sans mt-0.5">Built & supported in the USA</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#0d0d0d] rounded-xl border border-[#1e1e1e] p-8">
              {/* Top gold line */}
              <div className="h-px bg-gradient-to-r from-gold/50 via-gold/20 to-transparent mb-8" />

              {status === 'sent' ? (
                <div className="text-center py-14">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gold/10 border border-gold/30 mx-auto mb-5">
                    <i className="ri-checkbox-circle-line text-gold text-3xl" />
                  </div>
                  <h3 className="font-serif text-2xl text-white mb-3">Message Received</h3>
                  <p className="text-white/40 font-sans text-sm max-w-sm mx-auto">
                    We&apos;ll be in touch within one business day. For immediate assistance:{' '}
                    <a href="tel:8665407575" className="text-gold hover:text-gold-light transition-colors">
                      866-540-7575
                    </a>
                  </p>
                </div>
              ) : (
                <form data-readdy-form id="kc-quality-consultation-form" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-white/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-2" htmlFor="name">
                        Full Name *
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        placeholder="Your full name"
                        className="w-full bg-[#111] border border-[#252525] rounded-lg px-4 py-3 text-white text-sm font-sans placeholder-white/15 focus:outline-none focus:border-gold/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-white/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-2" htmlFor="phone">
                        Phone Number *
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder="Your phone number"
                        className="w-full bg-[#111] border border-[#252525] rounded-lg px-4 py-3 text-white text-sm font-sans placeholder-white/15 focus:outline-none focus:border-gold/40 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-white/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-2" htmlFor="email">
                        Email Address *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="your@email.com"
                        className="w-full bg-[#111] border border-[#252525] rounded-lg px-4 py-3 text-white text-sm font-sans placeholder-white/15 focus:outline-none focus:border-gold/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-white/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-2" htmlFor="project_type">
                        System Type
                      </label>
                      <select
                        id="project_type"
                        name="project_type"
                        className="w-full bg-[#111] border border-[#252525] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-gold/40 transition-colors cursor-pointer"
                      >
                        <option value="" className="bg-[#111]">Select a system type</option>
                        {PROJECT_TYPES.map((t) => (
                          <option key={t} value={t} className="bg-[#111]">{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-2" htmlFor="message">
                      Project Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder="Describe your cleaning or finishing requirements, parts types, volumes, and any specific needs..."
                      className="w-full bg-[#111] border border-[#252525] rounded-lg px-4 py-3 text-white text-sm font-sans placeholder-white/15 focus:outline-none focus:border-gold/40 transition-colors resize-none"
                      onChange={(e) => setCharCount(e.target.value.length)}
                    />
                    <div className="flex justify-between mt-1.5">
                      <span className="text-white/15 text-xs font-sans">Max 500 characters</span>
                      <span className={`text-xs font-sans ${charCount > 480 ? 'text-red-400' : 'text-white/15'}`}>
                        {charCount}/500
                      </span>
                    </div>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400/70 text-sm font-sans">
                      Something went wrong. Please try again or call us at 866-540-7575.
                    </p>
                  )}

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full py-4 bg-gold text-black font-bold text-sm font-sans rounded-lg hover:bg-gold-light transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 group"
                    >
                      {status === 'sending' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Schedule Your Consultation
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                    <p className="text-white/20 text-xs font-sans text-center mt-3">
                      We typically respond within one business day. Mon–Fri, 8am–5pm CST.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
